/* eslint-disable @typescript-eslint/no-explicit-any */
import { useIntl } from 'react-intl';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Typography,
  Divider,
  Button,
  Input,
  Space,
  Empty,
  Statistic,
} from 'antd';

import moment from 'moment';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  useDefaultEmptyFieldError,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import {
  CompaniesInfiniteSelectField,
  InputSearch,
} from '@/components/_common';
import { getTableScrollX, tableSorterOrder } from '@/utils/table';
import { Purchase } from '@/core/domain/purchase/Purchase.types';
import {
  PurchaseGroupingFormValues,
  PurchaseGroupingQuery,
  PurchasingGroupColumnConfig,
} from '@/core/domain/purchase/PurchaseGroup/PurchaseGroup.types';
import { usePurchasesGrouping } from '@/core/domain/purchase/Purchase.hooks';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import { useHistory } from 'react-router-dom';
import formatterPrice from '@/utils/number/formatterPrice';
import { SorterResult } from 'antd/lib/table/interface';

interface Props {
  form: FormInstance<PurchaseGroupingFormValues>;
  totalItens?: number;
  callback: () => void;
  queryParams: PurchaseGroupingQuery;
  setQueryParams: React.Dispatch<React.SetStateAction<PurchaseGroupingQuery>>;
  setPurchasesSelected: (purchase: Purchase[]) => void;
  purchaseCreated?: Purchase;
  handlePurchaseCreatedReset: () => void;
}

function PurchasingGroupingForm({
  form,
  totalItens,
  callback,
  queryParams,
  setQueryParams,
  setPurchasesSelected,
  purchaseCreated,
  handlePurchaseCreatedReset,
}: Props) {
  const intl = useIntl();
  const { Title } = Typography;
  const { isMobile } = useWindowSize();
  const history = useHistory();

  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const {
    data: purchasesData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    remove,
  } = usePurchasesGrouping(queryParams);

  const purchases = flatInfiniteData(purchasesData);

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: {
        id: supplier?.id,
        name: supplier?.name,
      },
    });
    modalSupplierAction.close();
  };

  const columns: ColumnsType<Purchase> = [
    {
      key: 'code',
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.code',
      }),
      ellipsis: true,
      width: 100,
      sorter: true,
    },
    {
      key: 'documentNumber',
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.documentNumber',
      }),
      width: 150,
      sorter: true,
    },
    {
      key: 'issueDate',
      dataIndex: 'issueDate',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.issuanceDate',
      }),
      ellipsis: true,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 80,
      sorter: true,
    },
    {
      key: 'releaseDate',
      dataIndex: 'releaseDate',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.releaseDateOf',
      }),
      ellipsis: true,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 80,
    },
    {
      key: 'departureDate',
      dataIndex: 'departureDate',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.departureDate',
      }),
      ellipsis: true,

      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 80,
    },
    {
      key: 'totalAmount',
      dataIndex: 'totalAmount',
      title: intl.formatMessage({
        id: 'purchasingGrouping.form.table.amount',
      }),
      ellipsis: true,
      render: (value) => formatterPrice(value),
      width: 100,
    },
  ];

  const handleOrderAccountPayable = (sorter: SorterResult<Purchase>) => {
    if (!sorter?.columnKey) return;

    const order = sorter.order ? tableSorterOrder[sorter.order] : undefined;
    const columnConfig = PurchasingGroupColumnConfig[sorter.columnKey];

    setQueryParams((prev) => {
      if (prev.orderByColumn === columnConfig) {
        return {
          ...prev,
          order,
        };
      }

      return {
        ...prev,
        order: tableSorterOrder.ascend,
        orderByColumn: columnConfig,
      };
    });
  };

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col lg={7} span={24} md={8}>
            <FloatingFormItem
              name="companyId"
              label={intl.formatMessage({
                id: 'purchasingGrouping.form.company',
              })}
              rules={[defaultEmptyFieldError]}
              initialValue={queryParams.companyId}
            >
              <CompaniesInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={7} xl={8}>
            <Form.Item
              hidden
              name={['supplier', 'id']}
              initialValue={queryParams.supplierId}
            >
              <Input />
            </Form.Item>
            <FloatingFormItem
              name={['supplier', 'name']}
              label={intl.formatMessage({
                id: 'purchases.releases.form.supplier',
              })}
              rules={[defaultEmptyFieldError]}
              initialValue={queryParams.supplierName}
            >
              <InputSearch
                inputProps={{ disabled: true }}
                buttonProps={{ onClick: modalSupplierAction.open }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={isMobile ? 24 : undefined}>
            <Button
              type="primary"
              style={{ marginTop: 5 }}
              onClick={() => {
                remove();
                callback();
              }}
              block={isMobile}
              size="large"
              loading={isFetching}
            >
              {intl.formatMessage({
                id: 'purchasingGrouping.form.button.searchForPurchases',
              })}
            </Button>
          </Col>

          {purchaseCreated && (
            <Col
              span={24}
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Space>
                <Button
                  type="primary"
                  block
                  onClick={handlePurchaseCreatedReset}
                >
                  Continuar no agrupamento
                </Button>
                <Button
                  type="primary"
                  block
                  onClick={() =>
                    history.push(
                      `/app/movements/purchases/purchasesReleases/${purchaseCreated.id}`,
                    )
                  }
                >
                  Ir até a compra gerada
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>

      {!purchaseCreated && !!purchases.length ? (
        <>
          <Divider />

          <InfiniteTable<Purchase>
            title={() => (
              <Title level={4} style={{ marginLeft: 20 }}>
                {intl.formatMessage({
                  id: 'purchasingGrouping.form.table.purchasesToBeGrouped',
                })}
              </Title>
            )}
            rowSelection={{
              onChange: (_rows, row) => setPurchasesSelected(row),
            }}
            dataSource={purchases}
            loading={isFetching}
            columns={columns}
            rowKey={(row) => row.id}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            scroll={{ x: getTableScrollX(columns) }}
            onChange={(_p, _f, sorter) => {
              if (!Array.isArray(sorter)) {
                handleOrderAccountPayable(sorter as SorterResult<Purchase>);
              }
            }}
            footer={() => (
              <Statistic
                formatter={(value) => formatterPrice(Number(value))}
                title="Valor total"
                value={totalItens}
                style={{ textAlign: 'right' }}
                valueStyle={{ color: '#a0ce33' }}
              />
            )}
          />
        </>
      ) : (
        <>
          <Divider />

          <Empty description="Não foi encontrada compras para agrupamento" />
        </>
      )}

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default PurchasingGroupingForm;
